import { useState } from 'react';
import { submitSupportRequest } from './firebase.js';

const products = {
  '/nahl-lens': {
    mark: 'NL',
    title: 'NahlLens',
    icon: '/assets/nahllens-icon.png',
    appStoreUrl: 'https://apps.apple.com/app/id6780807278',
    arabicTitle: 'عدسة النحل',
    subtitle: 'Varroa scanner for hive-frame field records.',
    description:
      'NahlLens helps beekeepers record hive-frame inspections with on-device computer vision, hive history, geohash grouping, and offline-first sync.',
    features: [
      'Create apiaries and hives before field scans.',
      'Review Varroa detection boxes in the camera view.',
      'Commit scan records with infestation percentage and severity.',
      'Group local scans by geohash for regional heatmap signals.',
      'Keep working offline and sync when connectivity returns.',
    ],
    disclosure:
      'NahlLens is a field record and decision-support tool. It does not replace professional agricultural inspection, treatment advice, or local authority guidance.',
    arabic:
      'يساعد NahlLens مربي النحل على تسجيل فحوصات إطارات الخلايا، حفظ السجل، ومزامنة البيانات عند عودة الاتصال. لا يحل محل الفحص الزراعي المتخصص أو إرشادات الجهات المحلية.',
  },
  '/waraq-lens': {
    mark: 'WL',
    title: 'WaraqLens',
    icon: '/assets/waraqlens-icon.png',
    appStoreUrl: 'https://apps.apple.com/app/id6780795394',
    arabicTitle: 'ورق لنس',
    subtitle: 'Plant checks, care notes, and journal history.',
    description:
      'WaraqLens helps gardeners and growers run guided three-leaf checks, save plant journal records, review care guidance, and sync regional plant signals.',
    features: [
      'Create a local garden list.',
      'Run guided three-leaf camera checks.',
      'Record Healthy, Watch, Treat, or Critical findings.',
      'Review journal history by plant.',
      'Keep working offline and sync when connectivity returns.',
    ],
    disclosure:
      'WaraqLens is a garden record and care-planning tool. It does not replace professional agronomy, laboratory diagnosis, pesticide instructions, or local agricultural guidance.',
    arabic:
      'يساعد WaraqLens البستانيين والمزارعين على حفظ فحوصات نباتية منظمة ويوميات عناية. لا يحل محل اختصاصي زراعي أو تشخيص مختبر أو تعليمات مبيدات.',
  },
};

function normalizePath() {
  const path = window.location.pathname.replace(/\/+$/, '');
  return path === '' ? '/' : path;
}

function currentProduct(path) {
  if (products[path]) return products[path];
  if (window.location.hostname.includes('waraqlens')) {
    return products['/waraq-lens'];
  }
  return products['/nahl-lens'];
}

function setPageTitle(title) {
  if (typeof document !== 'undefined') {
    document.title = title;
  }
}

function privacyCopy(product) {
  if (product.title === 'WaraqLens') {
    return {
      dataRecords:
        'Inspection records you create, such as plant names, guided three-leaf check results, leaf severities, rolled-up severity, sync status, and timestamps.',
      productNotes:
        'WaraqLens saves guided three-leaf plant check records such as selected plant, leaf severities, rolled-up severity, confidence, geohash, app version, and sync status.',
      arabic:
        'يستخدم WaraqLens الكاميرا لفحوصات النبات، والموقع عند منح الإذن لتجميع السجلات جغرافيا، وهوية Firebase مجهولة للمزامنة. لا يحتوي التطبيق على إعلانات ولا يستخدم البيانات للتتبع عبر التطبيقات أو المواقع.',
      supportScope:
        'WaraqLens support covers guided plant checks, garden records, journal history, care notes, regional signals, sync, privacy questions, and deletion requests.',
    };
  }

  return {
    dataRecords:
      'Inspection records you create, such as apiary names, hive labels, scan results, infestation metrics, severity, sync status, and timestamps.',
    productNotes:
      'NahlLens saves hive scan records such as selected apiary, selected hive, frame count, Varroa count, infestation percentage, confidence, severity, geohash, app version, and sync status.',
    arabic:
      'يستخدم NahlLens الكاميرا لفحوصات الخلايا، والموقع عند منح الإذن لتجميع السجلات جغرافيا، وهوية Firebase مجهولة للمزامنة. لا يحتوي التطبيق على إعلانات ولا يستخدم البيانات للتتبع عبر التطبيقات أو المواقع.',
    supportScope:
      'NahlLens support covers hive-frame scan records, apiaries, hives, heatmap signals, sync, privacy questions, and deletion requests.',
  };
}

export function StorePageRouter({ children }) {
  const path = normalizePath();
  const activeProduct = currentProduct(path);

  if (path === '/') {
    setPageTitle(`${activeProduct.title} - Store Information`);
    return <ProductPage product={activeProduct} />;
  }

  if (products[path]) {
    setPageTitle(`${products[path].title} - Store Information`);
    return <ProductPage product={products[path]} />;
  }

  if (path === '/privacy') {
    setPageTitle(`${activeProduct.title} - Privacy Policy`);
    return <PrivacyPage activeProduct={activeProduct} />;
  }

  if (path === '/support') {
    setPageTitle(`${activeProduct.title} - Support`);
    return <SupportPage activeProduct={activeProduct} />;
  }

  setPageTitle(`${activeProduct.title} - Command Center`);
  return children;
}

function StoreShell({ children, activeProduct = products['/nahl-lens'] }) {
  return (
    <div className="store-site">
      <header className="store-nav">
        <a
          className="store-brand"
          href={activeProduct.title === 'WaraqLens' ? '/waraq-lens' : '/nahl-lens'}
          aria-label={`${activeProduct.title} home`}
        >
          <img className="store-brand__icon" src={activeProduct.icon} alt="" />
          <span>{activeProduct.title}</span>
        </a>
        <nav aria-label="Store pages">
          <a href="/nahl-lens">NahlLens</a>
          <a href="/waraq-lens">WaraqLens</a>
          <a href="/privacy">Privacy</a>
          <a href="/support">Support</a>
        </nav>
      </header>
      {children}
      <footer className="store-footer">
        <span>Made with love by Mahran Bayed</span>
        <a href="https://mahranbayed.com" rel="noreferrer" target="_blank">
          mahranbayed.com
        </a>
      </footer>
    </div>
  );
}

function ProductPage({ product }) {
  return (
    <StoreShell activeProduct={product}>
      <main className="store-main">
        <section className="store-hero">
          <div className="store-hero__copy">
            <img className="product-icon" src={product.icon} alt="" />
            <h1>{product.title}</h1>
            <p className="store-lead">{product.subtitle}</p>
            <p>{product.description}</p>
            <div className="store-actions">
              <a href="/privacy" className="store-button store-button--primary">
                Privacy details
              </a>
              <a href="/support" className="store-button">
                Contact support
              </a>
              <a
                href={product.appStoreUrl}
                className="store-button"
                rel="noreferrer"
                target="_blank"
              >
                View on App Store
              </a>
            </div>
          </div>
        </section>

        <section className="store-section">
          <div>
            <h2>What It Covers</h2>
            <p>
              These pages support the published store listings and give users
              and reviewers a public product, privacy, and support reference.
            </p>
          </div>
          <div className="feature-list">
            {product.features.map((feature) => (
              <article key={feature}>
                <span />
                <p>{feature}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="store-section store-section--two">
          <div>
            <h2>Important Limit</h2>
            <p>{product.disclosure}</p>
          </div>
          <div lang="ar" dir="rtl">
            <h2>{product.arabicTitle}</h2>
            <p>{product.arabic}</p>
          </div>
        </section>
      </main>
    </StoreShell>
  );
}

function PrivacyPage({ activeProduct }) {
  const copy = privacyCopy(activeProduct);

  return (
    <StoreShell activeProduct={activeProduct}>
      <main className="store-main store-document">
        <section className="store-document__header">
          <h1>Privacy Policy</h1>
          <p>
            This Privacy Policy explains what {activeProduct.title} records
            when you use the app. The app stores inspection results for app
            functionality, optional sync, diagnostics, analytics, and security.
            {activeProduct.title} does not include ads and does not sell
            personal data.
          </p>
        </section>

        <section>
          <h2>Data Recorded by the App</h2>
          <ul>
            <li>Camera permission is used for scan capture and on-device analysis.</li>
            <li>Optional location can be attached to saved inspection records as latitude, longitude, accuracy, timestamp, and geohash when location permission is granted.</li>
            <li>An anonymous Firebase Authentication ID and Firebase installation identifiers are used for sync, diagnostics, analytics, and app security.</li>
            <li>{copy.dataRecords}</li>
            <li>Crash logs, diagnostics, and limited analytics events may be recorded through Firebase services.</li>
          </ul>
        </section>

        <section>
          <h2>How Records Are Used</h2>
          <p>
            Inspection records are used to show your app history, support
            offline-first sync, provide regional geohash grouping, improve app
            reliability, and protect the service. {activeProduct.title} does not
            include ads and does not use records for cross-app or cross-site
            tracking.
          </p>
        </section>

        <section>
          <h2>Firebase Services</h2>
          <p>
            The apps use anonymous Firebase Authentication, Firestore sync,
            Firebase Analytics, and Firebase Crashlytics. Firebase acts as a
            service provider for app functionality, diagnostics, analytics, and
            abuse or security protection.
          </p>
        </section>

        <section>
          <h2>Product Notes</h2>
          <p>{copy.productNotes}</p>
        </section>

        <section>
          <h2>Support and Deletion Requests</h2>
          <p>
            Users can request support, account help, or deletion of app records
            through the support form. Include the app name, device model,
            operating system version, and a short description of the request.
          </p>
        </section>

        <section lang="ar" dir="rtl">
          <h2>ملخص الخصوصية</h2>
          <p>{copy.arabic}</p>
        </section>
      </main>
    </StoreShell>
  );
}

function SupportPage({ activeProduct }) {
  const copy = privacyCopy(activeProduct);
  const [form, setForm] = useState({
    name: '',
    replyEmail: '',
    category: 'support',
    message: '',
    device: '',
    osVersion: '',
    company: '',
  });
  const [submitState, setSubmitState] = useState({
    status: 'idle',
    message: '',
  });

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    if (submitState.status === 'error') {
      setSubmitState({ status: 'idle', message: '' });
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (form.company.trim()) return;

    const message = form.message.trim();
    if (message.length < 20) {
      setSubmitState({
        status: 'error',
        message: 'Message must be at least 20 characters.',
      });
      return;
    }
    if (message.length > 4000) {
      setSubmitState({
        status: 'error',
        message: 'Message must be 4000 characters or fewer.',
      });
      return;
    }

    setSubmitState({ status: 'submitting', message: 'Sending request...' });
    try {
      await submitSupportRequest({
        category: form.category,
        name: form.name.trim(),
        replyEmail: form.replyEmail.trim(),
        message,
        device: form.device.trim(),
        osVersion: form.osVersion.trim(),
      });
      setForm({
        name: '',
        replyEmail: '',
        category: 'support',
        message: '',
        device: '',
        osVersion: '',
        company: '',
      });
      setSubmitState({
        status: 'success',
        message: 'Support request saved. It will be reviewed from Firebase.',
      });
    } catch (error) {
      setSubmitState({
        status: 'error',
        message: `Could not save request. ${error.message}`,
      });
    }
  }

  return (
    <StoreShell activeProduct={activeProduct}>
      <main className="store-main store-document">
        <section className="store-document__header">
          <h1>Support</h1>
          <p>
            Support for {activeProduct.title} is handled through this form.
            Requests are stored securely and reviewed from Firebase.
          </p>
        </section>

        <section className="support-contact">
          <h2>Send Request</h2>
          <form className="support-form" onSubmit={handleSubmit}>
            <label className="support-honeypot">
              Company
              <input
                autoComplete="off"
                tabIndex="-1"
                value={form.company}
                onChange={(event) => updateField('company', event.target.value)}
              />
            </label>

            <label>
              App
              <input readOnly value={activeProduct.title} />
            </label>

            <label>
              Category
              <select
                value={form.category}
                onChange={(event) => updateField('category', event.target.value)}
              >
                <option value="support">Support</option>
                <option value="privacy">Privacy</option>
                <option value="delete_data">Delete data</option>
                <option value="bug">Bug report</option>
                <option value="other">Other</option>
              </select>
            </label>

            <label>
              Name optional
              <input
                maxLength="120"
                value={form.name}
                onChange={(event) => updateField('name', event.target.value)}
              />
            </label>

            <label>
              Reply email optional
              <input
                maxLength="254"
                type="email"
                value={form.replyEmail}
                onChange={(event) => updateField('replyEmail', event.target.value)}
              />
            </label>

            <label>
              Device optional
              <input
                maxLength="120"
                placeholder="Example: iPhone 15"
                value={form.device}
                onChange={(event) => updateField('device', event.target.value)}
              />
            </label>

            <label>
              OS version optional
              <input
                maxLength="120"
                placeholder="Example: iOS 18"
                value={form.osVersion}
                onChange={(event) => updateField('osVersion', event.target.value)}
              />
            </label>

            <label>
              Message
              <textarea
                required
                minLength="20"
                maxLength="4000"
                rows="6"
                value={form.message}
                onChange={(event) => updateField('message', event.target.value)}
              />
            </label>

            {submitState.message && (
              <p className={`support-status support-status--${submitState.status}`}>
                {submitState.message}
              </p>
            )}

            <button
              className="store-button store-button--primary"
              disabled={submitState.status === 'submitting'}
              type="submit"
            >
              {submitState.status === 'submitting' ? 'Sending...' : 'Submit support request'}
            </button>
          </form>
        </section>

        <section>
          <h2>App Covered</h2>
          <p>{copy.supportScope}</p>
        </section>

        <section>
          <h2>Privacy and Data Requests</h2>
          <p>
            For privacy questions or deletion requests, include the app name and
            the Firebase account state shown in the app if available. Requests
            are handled through this support form.
          </p>
        </section>

        <section lang="ar" dir="rtl">
          <h2>الدعم</h2>
          <p>
            يتوفر الدعم عبر البريد الإلكتروني لتطبيق {activeProduct.title}.
            يرجى تضمين نوع الجهاز وإصدار نظام التشغيل ووصفا
            مختصرا للمشكلة أو الطلب.
          </p>
        </section>
      </main>
    </StoreShell>
  );
}
